import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatarDataBR } from '../common/data';

interface DiarioDia {
  id: number; data: string;
  alimentacao?: any; higiene?: any; sono?: any; humor?: any; atividades?: any; ocorrencias?: any; presenca?: any;
  observacoes_gerais?: string;
}
interface EnqueteDoComunicado {
  opcoes: Array<{ id: number; texto: string }>;
  /** A opção que ESTA família escolheu — sem isso ela não sabe o que respondeu. */
  meu_voto: number | null;
  encerrada: boolean;
  encerra_em?: string | null;
}
interface Comunicado {
  id: number; tipo: string; titulo?: string; mensagem?: string; lido_em?: string | null; criado_em?: string;
  enquete?: EnqueteDoComunicado;
}
interface Boleto { id: number; vencimento: string; valor: number; status: string; }
interface Avaliacao { id: number; data: string; campo_experiencia?: string | null; relatorio?: string | null; criterio?: string | null; }
interface Termo { id: number; tipo: string; titulo: string; conteudo?: string | null; status: string; assinado_em?: string | null; }
interface Ausencia { data: string; status: string; justificativa: string; }
interface Frequencia {
  periodoDias: number; total: number; presencas: number; faltas: number;
  justificadas: number; frequenciaPct: number | null; ausencias: Ausencia[];
}

interface DiaCardapio {
  data: string; dia_semana: string;
  cafe_manha?: string | null; lanche_manha?: string | null; almoco?: string | null;
  lanche_tarde?: string | null; jantar?: string | null; observacoes?: string | null;
  daTurma: boolean;
}
interface RegistroAceitacao { data: string; refeicao: string; nivel: string; observacao: string; }
interface Alimentacao {
  semana: { inicio: string; fim: string };
  cardapio: DiaCardapio[];
  aceitacao: {
    periodoDias: number; registros: number; indice: number | null;
    aceitouTudo: number; aceitouParte: number; aceitouPouco: number; recusou: number;
    semRegistro: boolean; observacao: string | null; recentes: RegistroAceitacao[];
  };
  restricoes: {
    alergias: string | null; restricoes_alimentares: string | null; nenhumaRegistrada: boolean;
  };
  podePropor: boolean;
  alternativas: Alternativa[];
}

interface Alternativa {
  id: number; refeicao: string | null; alternativa: string; motivo: string | null;
  status: 'pendente' | 'aceita' | 'recusada'; resposta: string | null;
  criado_em?: string; respondido_em?: string | null;
}

interface MeuFilho {
  aluno: { id: number; nome: string; foto_url: string | null; turma: string | null };
  frequencia?: Frequencia;
  alimentacao?: Alimentacao;
  diario: DiarioDia[];
  comunicados: Comunicado[];
  desenvolvimento: Avaliacao[];
  termos: Termo[];
  financeiro: { aReceber: number; proximoVencimento: string | null; boletos: Boleto[] };
}

const TIPO_TERMO_LABEL: Record<string, string> = {
  contrato_matricula: 'Contrato de matrícula',
  uso_imagem: 'Uso de imagem',
  saida_pedagogica: 'Saída pedagógica',
  autorizacao_medicamento: 'Autorização de medicamento',
  lgpd_dados: 'Consentimento LGPD',
  outro: 'Documento',
};

const brl = (v: number) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Duas funções porque são dois tipos de dado, e usar uma só quebra metade.
 *
 * `dataBR` é para coluna **DATE** (vencimento, data da ausência, dia do
 * cardápio): `new Date('2026-08-10')` é lido como meia-noite UTC e volta 09/08
 * em Brasília — o bug do PR #82, que estava vivo nesta tela.
 *
 * `dataHoraBR` é para **datetime** de verdade (criado_em, assinado_em), onde a
 * conversão de fuso é justamente o que se quer. Apesar do nome, ele mostra só
 * o dia — o que basta para "quando isto foi enviado".
 *
 * `prazoBR` mostra o dia E A HORA, para o único caso em que a hora decide
 * alguma coisa: o fechamento da enquete. "Encerra em 20/08" deixa a família sem
 * saber se ainda dá tempo de responder à noite.
 */
const dataBR = (s?: string | null) => formatarDataBR(s) || '—';
const dataHoraBR = (s?: string | null) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—');
const prazoBR = (s?: string | null) =>
  s ? new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function mostrarValor(v: any): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(mostrarValor).filter(Boolean).join(', ');
  if (typeof v === 'object') return Object.entries(v).map(([k, val]) => `${k}: ${mostrarValor(val)}`).filter(Boolean).join(' · ');
  return String(v);
}

const REFEICAO_LABEL: Record<string, string> = {
  cafe_manha: 'Café da manhã',
  lanche_manha: 'Lanche da manhã',
  almoco: 'Almoço',
  lanche_tarde: 'Lanche da tarde',
  jantar: 'Jantar',
};

/** Linguagem de pai, não de sistema: "recusou" é mais claro que "nível 0". */
const NIVEL_LABEL: Record<string, string> = {
  aceitou_tudo: 'Comeu tudo',
  aceitou_parte: 'Comeu boa parte',
  aceitou_pouco: 'Comeu pouco',
  recusou: 'Recusou',
};

const NIVEL_COR: Record<string, string> = {
  aceitou_tudo: '#2e7d32',
  aceitou_parte: '#558b2f',
  aceitou_pouco: '#e65100',
  recusou: '#c62828',
};

const ICONES: Record<string, string> = {
  alimentacao: '🍽️', higiene: '🧷', sono: '😴', humor: '🙂', atividades: '🎨', ocorrencias: '⚠️', presenca: '✅',
};

function CampoDiario({ campo, valor }: { campo: string; valor: any }) {
  const texto = mostrarValor(valor);
  if (!texto) return null;
  return (
    <div style={{ fontSize: 13, margin: '2px 0' }}>
      <span style={{ marginRight: 6 }}>{ICONES[campo] || '•'}</span>
      <strong style={{ textTransform: 'capitalize' }}>{campo}:</strong> {texto}
    </div>
  );
}

export default function PortalPaisPage() {
  const [dados, setDados] = useState<MeuFilho | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [erroComunicado, setErroComunicado] = useState('');

  // Proposta de alternativa de cardápio
  const [propRefeicao, setPropRefeicao] = useState('');
  const [propAlternativa, setPropAlternativa] = useState('');
  const [propMotivo, setPropMotivo] = useState('');
  const [enviandoProp, setEnviandoProp] = useState(false);
  const [erroProp, setErroProp] = useState('');
  const [msgProp, setMsgProp] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const { data } = await api.get<MeuFilho>('/portal-pais/meu-filho');
      setDados(data);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar as informações do seu filho(a).');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  /**
   * O erro NÃO é mais silencioso.
   *
   * Enquanto era, o comunicado geral da creche respondia 403 e o botão
   * simplesmente não fazia nada: a mãe clicava, a tela não mudava, e ninguém
   * — nem ela, nem a creche — ficava sabendo. Falha de gravação que some é
   * pior do que falha que aparece.
   */
  const marcarLido = async (id: number) => {
    try {
      await api.post(`/portal-pais/comunicados/${id}/lido`);
      setDados((d) => d ? { ...d, comunicados: d.comunicados.map((c) => c.id === id ? { ...c, lido_em: new Date().toISOString() } : c) } : d);
    } catch (e: any) {
      setErroComunicado(e?.response?.data?.message || 'Não foi possível registrar a leitura. Tente novamente.');
      setTimeout(() => setErroComunicado(''), 6000);
    }
  };

  /** A família responde a enquete. Pode trocar a resposta enquanto estiver aberta. */
  const votar = async (idComunicado: number, idOpcao: number) => {
    setErroComunicado('');
    try {
      await api.post(`/portal-pais/enquetes/${idComunicado}/votar`, { id_opcao: idOpcao });
      setDados((d) => d ? {
        ...d,
        comunicados: d.comunicados.map((c) =>
          c.id === idComunicado && c.enquete
            ? { ...c, enquete: { ...c.enquete, meu_voto: idOpcao } }
            : c,
        ),
      } : d);
    } catch (e: any) {
      setErroComunicado(e?.response?.data?.message || 'Não foi possível registrar seu voto.');
      setTimeout(() => setErroComunicado(''), 8000);
    }
  };

  const enviarAlternativa = async () => {
    if (!propAlternativa.trim()) { setErroProp('Descreva a alternativa que você propõe.'); return; }
    setEnviandoProp(true); setErroProp(''); setMsgProp('');
    try {
      await api.post('/portal-pais/alimentacao/alternativas', {
        refeicao: propRefeicao || null,
        alternativa: propAlternativa,
        motivo: propMotivo || null,
      });
      setPropAlternativa(''); setPropMotivo(''); setPropRefeicao('');
      setMsgProp('Proposta enviada. A creche vai avaliar e responder por aqui.');
      carregar();
      setTimeout(() => setMsgProp(''), 5000);
    } catch (e: any) {
      setErroProp(e?.response?.data?.message || 'Não foi possível enviar a proposta.');
    } finally { setEnviandoProp(false); }
  };

  const aceitarTermo = async (t: Termo) => {
    if (!window.confirm(`Você confirma que LEU e AUTORIZA "${t.titulo}"?\n\nSeu aceite será registrado com data, hora e IP, tendo valor de assinatura.`)) return;
    try {
      const { data } = await api.post<{ assinado_em?: string }>(`/portal-pais/termos/${t.id}/aceitar`);
      setDados((d) => d ? { ...d, termos: d.termos.map((x) => x.id === t.id ? { ...x, status: 'assinado', assinado_em: data?.assinado_em || new Date().toISOString() } : x) } : d);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Não foi possível registrar o aceite.');
    }
  };

  if (carregando) return <div style={{ padding: 24 }}>Carregando…</div>;
  if (erro) return <div style={{ padding: 24, color: '#c62828' }}>{erro}</div>;
  if (!dados) return null;

  const { aluno, diario, comunicados, desenvolvimento, termos, financeiro, frequencia, alimentacao } = dados;
  const naoLidos = comunicados.filter((c) => !c.lido_em).length;
  const termosPendentes = termos.filter((t) => t.status === 'pendente').length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Cabeçalho da criança */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20 }}>
        {aluno.foto_url
          ? <img src={aluno.foto_url} alt={aluno.nome} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
          : <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ffe0ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🧒</div>}
        <div>
          <h2 style={{ margin: 0 }}>{aluno.nome}</h2>
          <div style={{ color: '#888', fontSize: 14 }}>{aluno.turma ? `Turma: ${aluno.turma}` : 'Portal dos Pais'}</div>
        </div>
        <button onClick={carregar} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer' }}>↻ Atualizar</button>
      </div>

      {/* Frequência do filho — a creche registrava e só ela via. Para a família
          é informação de primeira ordem: confere o dia, percebe o padrão e lê a
          justificativa que foi registrada em nome dela. */}
      {frequencia && frequencia.total > 0 && frequencia.frequenciaPct !== null && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 10px' }}>🗓️ Frequência</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ flex: '1 1 200px', background: '#fff', border: '1px solid #eee', borderLeft: `4px solid ${frequencia.frequenciaPct >= 75 ? '#2e7d32' : frequencia.frequenciaPct >= 60 ? '#e65100' : '#c62828'}`, borderRadius: 10, padding: 14 }}>
              <div style={{ color: '#888', fontSize: 13 }}>Presença nos últimos {frequencia.periodoDias} dias</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{frequencia.frequenciaPct}%</div>
            </div>
            <div style={{ flex: '1 1 200px', background: '#fff', border: '1px solid #eee', borderLeft: '4px solid #1565c0', borderRadius: 10, padding: 14 }}>
              <div style={{ color: '#888', fontSize: 13 }}>Dias registrados</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>
                {frequencia.presencas} de {frequencia.total}
              </div>
            </div>
          </div>

          {frequencia.ausencias.length === 0 ? (
            <div style={{ color: '#2e7d32', fontSize: 14 }}>✅ Sem faltas registradas no período.</div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 12 }}>
              <div style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>
                Dias em que {aluno.nome.split(' ')[0]} não esteve presente:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {frequencia.ausencias.map((a, i) => (
                  <div key={`${a.data}-${i}`} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 14, borderTop: i === 0 ? 'none' : '1px solid #f5f5f5', paddingTop: i === 0 ? 0 : 6 }}>
                    <strong style={{ minWidth: 92 }}>{dataBR(a.data)}</strong>
                    <span style={{ color: a.status === 'ausente' ? '#c62828' : a.status === 'justificado' ? '#e65100' : '#1565c0' }}>
                      {a.status === 'ausente' ? '❌ Falta' : a.status === 'justificado' ? '📝 Justificada' : '⏰ Atraso'}
                    </span>
                    {a.justificativa && <span style={{ color: '#666' }}>— {a.justificativa}</span>}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 12.5, color: '#888' }}>
                Alguma falta está registrada errado? Fale com a secretaria da creche.
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── ALIMENTAÇÃO ──
          O cardápio existia e só a creche via — o mesmo meio caminho da
          frequência antes do pilar #2. Aqui ele responde as duas perguntas que a
          família faz na porta da creche: "o que ele almoçou?" e "ele comeu?". */}
      {alimentacao && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 10px' }}>🍽️ Alimentação</h3>

          {/* Restrições que a creche tem cadastradas — para a família CONFERIR.
              Quem sabe que a alergia mudou é ela, e até agora não tinha como
              verificar se a creche estava com a informação certa. */}
          <div style={{
            background: alimentacao.restricoes.nenhumaRegistrada ? '#f8fafc' : '#fff7ed',
            border: `1px solid ${alimentacao.restricoes.nenhumaRegistrada ? '#e2e8f0' : '#fed7aa'}`,
            borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 14,
          }}>
            <strong>Restrições alimentares registradas na creche</strong>
            {alimentacao.restricoes.nenhumaRegistrada ? (
              <div style={{ color: '#64748b', marginTop: 4 }}>
                Nenhuma restrição cadastrada. Se seu filho(a) tem alergia ou restrição,
                avise a secretaria — é essa informação que gera o alerta na cozinha.
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                {alimentacao.restricoes.alergias && <div>⚠️ Alergias: {alimentacao.restricoes.alergias}</div>}
                {alimentacao.restricoes.restricoes_alimentares && (
                  <div>🥗 Restrições: {alimentacao.restricoes.restricoes_alimentares}</div>
                )}
                <div style={{ color: '#92400e', fontSize: 12.5, marginTop: 6 }}>
                  Está desatualizado ou faltando alguma coisa? Fale com a secretaria.
                </div>
              </div>
            )}
          </div>

          {/* Cardápio da semana */}
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Cardápio de {dataBR(alimentacao.semana.inicio)} a {dataBR(alimentacao.semana.fim)}
            </div>
            {alimentacao.cardapio.length === 0 ? (
              <div style={{ color: '#888', fontSize: 14 }}>
                A creche ainda não publicou o cardápio desta semana.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {alimentacao.cardapio.map((d) => (
                  <div key={d.data} style={{ borderTop: '1px solid #f5f5f5', paddingTop: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                      {dataBR(d.data)}
                      {d.daTurma && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#0f766e', background: '#f0fdfa', borderRadius: 999, padding: '2px 8px' }}>
                          cardápio da turma
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {d.cafe_manha && <span>☕ Café: {d.cafe_manha}</span>}
                      {d.lanche_manha && <span>🍌 Lanche da manhã: {d.lanche_manha}</span>}
                      {d.almoco && <span>🍲 Almoço: {d.almoco}</span>}
                      {d.lanche_tarde && <span>🥛 Lanche da tarde: {d.lanche_tarde}</span>}
                      {d.jantar && <span>🌙 Jantar: {d.jantar}</span>}
                      {d.observacoes && <span style={{ color: '#64748b' }}>ℹ️ {d.observacoes}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Aceitação — "ele comeu?" */}
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Como ele(a) tem comido — últimos {alimentacao.aceitacao.periodoDias} dias
            </div>

            {/* Sem registro NÃO é "não comeu". A tela diz isso com todas as
                letras, porque o silêncio da creche não pode virar preocupação
                inventada em cima de dado que não existe. */}
            {alimentacao.aceitacao.semRegistro ? (
              <div style={{ color: '#888', fontSize: 14 }}>{alimentacao.aceitacao.observacao}</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div style={{
                    flex: '1 1 180px', border: '1px solid #eee',
                    borderLeft: `4px solid ${(alimentacao.aceitacao.indice ?? 0) >= 66 ? '#2e7d32' : (alimentacao.aceitacao.indice ?? 0) >= 40 ? '#e65100' : '#c62828'}`,
                    borderRadius: 10, padding: 12,
                  }}>
                    <div style={{ color: '#888', fontSize: 13 }}>Aceitação das refeições</div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{alimentacao.aceitacao.indice}%</div>
                  </div>
                  <div style={{ flex: '1 1 180px', border: '1px solid #eee', borderLeft: '4px solid #1565c0', borderRadius: 10, padding: 12 }}>
                    <div style={{ color: '#888', fontSize: 13 }}>Refeições registradas</div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{alimentacao.aceitacao.registros}</div>
                  </div>
                </div>

                <div style={{ fontSize: 13.5, color: '#475569', marginBottom: 8 }}>
                  Comeu tudo: {alimentacao.aceitacao.aceitouTudo} · boa parte: {alimentacao.aceitacao.aceitouParte} ·
                  {' '}pouco: {alimentacao.aceitacao.aceitouPouco} · recusou: {alimentacao.aceitacao.recusou}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {alimentacao.aceitacao.recentes.map((r, i) => (
                    <div key={`${r.data}-${r.refeicao}-${i}`} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 13.5, borderTop: i === 0 ? 'none' : '1px solid #f5f5f5', paddingTop: i === 0 ? 0 : 4 }}>
                      <strong style={{ minWidth: 92 }}>{dataBR(r.data)}</strong>
                      <span style={{ minWidth: 120, color: '#64748b' }}>{REFEICAO_LABEL[r.refeicao] || r.refeicao}</span>
                      <span style={{ color: NIVEL_COR[r.nivel] || '#334155', fontWeight: 600 }}>
                        {NIVEL_LABEL[r.nivel] || r.nivel}
                      </span>
                      {r.observacao && <span style={{ color: '#666' }}>— {r.observacao}</span>}
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 10, fontSize: 12.5, color: '#888' }}>
                  Registro feito pela educadora observando o prato. Refeição sem registro não entra na conta —
                  não significa que a criança não comeu.
                </div>
              </>
            )}
          </div>

          {/* ── PROPOR ALTERNATIVA ──
              Só para criança com regime diferenciado registrado. Quem convive
              com a restrição sabe o que a criança aceita e o que já causou
              reação — hoje isso chega por bilhete e se perde. */}
          {alimentacao.podePropor && (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14, marginTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Propor uma alternativa de cardápio</div>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>
                Seu filho(a) tem restrição alimentar registrada. Se você tem uma substituição que funciona
                bem em casa, ou uma orientação do pediatra/nutricionista, envie para a creche avaliar.
              </p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
                <label style={{ fontSize: 13 }}>Refeição
                  <select value={propRefeicao} onChange={(e) => setPropRefeicao(e.target.value)} style={{ display: 'block' }}>
                    <option value="">Todas as refeições</option>
                    {Object.entries(REFEICAO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label style={{ flex: '1 1 240px', fontSize: 13 }}>O que propõe servir no lugar
                  <input value={propAlternativa} onChange={(e) => setPropAlternativa(e.target.value)}
                    placeholder="Ex.: bebida vegetal de aveia no lugar do leite"
                    style={{ display: 'block', width: '100%' }} />
                </label>
                <label style={{ flex: '1 1 240px', fontSize: 13 }}>Motivo (opcional)
                  <input value={propMotivo} onChange={(e) => setPropMotivo(e.target.value)}
                    placeholder="Ex.: orientação da nutricionista, já usamos em casa"
                    style={{ display: 'block', width: '100%' }} />
                </label>
                <button onClick={enviarAlternativa} disabled={enviandoProp}
                  style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: enviandoProp ? '#9cc7a2' : '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  {enviandoProp ? 'Enviando…' : 'Enviar proposta'}
                </button>
              </div>
              {erroProp && <div style={{ color: '#c62828', marginTop: 8, fontSize: 13.5 }}>{erroProp}</div>}
              {msgProp && <div style={{ color: '#2e7d32', marginTop: 8, fontSize: 13.5 }}>{msgProp}</div>}

              {alimentacao.alternativas.length > 0 && (
                <div style={{ marginTop: 14, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 6 }}>Suas propostas</div>
                  {alimentacao.alternativas.map((a) => (
                    <div key={a.id} style={{
                      border: '1px solid #f0f0f0',
                      borderLeft: `4px solid ${a.status === 'aceita' ? '#2e7d32' : a.status === 'recusada' ? '#c62828' : '#e65100'}`,
                      borderRadius: 8, padding: '8px 12px', marginBottom: 6, fontSize: 13.5,
                    }}>
                      <div>
                        <strong>
                          {a.status === 'aceita' ? '✅ Aceita' : a.status === 'recusada' ? '❌ Recusada' : '⏳ Aguardando a creche'}
                        </strong>
                        <span style={{ color: '#64748b', marginLeft: 8 }}>
                          {a.refeicao ? REFEICAO_LABEL[a.refeicao] : 'Todas as refeições'}
                        </span>
                      </div>
                      <div style={{ marginTop: 2 }}>{a.alternativa}</div>
                      {/* A creche é obrigada a justificar a recusa — e a família lê aqui. */}
                      {a.resposta && (
                        <div style={{ marginTop: 4, color: '#475569' }}>
                          <strong>Resposta da creche:</strong> {a.resposta}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Financeiro do filho */}
      <section style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 10px' }}>💰 Financeiro</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ flex: '1 1 200px', background: '#fff', border: '1px solid #eee', borderLeft: '4px solid #1565c0', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>EM ABERTO</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{brl(financeiro.aReceber)}</div>
          </div>
          <div style={{ flex: '1 1 200px', background: '#fff', border: '1px solid #eee', borderLeft: '4px solid #e65100', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>PRÓXIMO VENCIMENTO</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{dataBR(financeiro.proximoVencimento)}</div>
          </div>
        </div>
        {financeiro.boletos.length === 0
          ? <div style={{ color: '#888', fontSize: 14 }}>Nenhuma cobrança registrada.</div>
          : (
            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Vencimento</th><th style={{ padding: '8px 12px' }}>Valor</th><th style={{ padding: '8px 12px' }}>Status</th>
                </tr></thead>
                <tbody>
                  {financeiro.boletos.map((b) => (
                    <tr key={b.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 12px' }}>{dataBR(b.vencimento)}</td>
                      <td style={{ padding: '8px 12px' }}>{brl(b.valor)}</td>
                      <td style={{ padding: '8px 12px' }}><span className={`badge badge-${String(b.status).toLowerCase()}`}>{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>

      {/* Comunicados */}
      <section style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 10px' }}>📣 Comunicados {naoLidos > 0 ? <span style={{ background: '#c62828', color: '#fff', borderRadius: 10, fontSize: 12, padding: '1px 8px' }}>{naoLidos} novo(s)</span> : null}</h3>
        {erroComunicado && (
          <div style={{ background: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 8 }}>
            {erroComunicado}
          </div>
        )}
        {comunicados.length === 0
          ? <div style={{ color: '#888', fontSize: 14 }}>Nenhum comunicado.</div>
          : comunicados.map((c) => (
            <div key={c.id} style={{ background: c.lido_em ? '#fff' : '#fff8e1', border: '1px solid #eee', borderRadius: 10, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{c.titulo || c.tipo}</strong>
                <span style={{ fontSize: 12, color: '#999' }}>{dataHoraBR(c.criado_em)}</span>
              </div>
              {c.mensagem && <div style={{ fontSize: 14, marginTop: 4, whiteSpace: 'pre-wrap' }}>{c.mensagem}</div>}

              {c.enquete && (
                <div style={{ marginTop: 10, background: '#f7f9ff', border: '1px solid #e3e9f7', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                    {c.enquete.encerrada
                      ? `Enquete encerrada${c.enquete.encerra_em ? ` em ${prazoBR(c.enquete.encerra_em)}` : ''}.`
                      : c.enquete.meu_voto
                        ? `Sua resposta está registrada. Você pode alterá-la${c.enquete.encerra_em ? ` até ${prazoBR(c.enquete.encerra_em)}` : ' enquanto a enquete estiver aberta'}.`
                        : `Escolha uma opção para responder${c.enquete.encerra_em ? ` até ${prazoBR(c.enquete.encerra_em)}` : ''}:`}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {c.enquete.opcoes.map((o) => {
                      const escolhida = c.enquete!.meu_voto === o.id;
                      return (
                        <button
                          key={o.id}
                          onClick={() => votar(c.id, o.id)}
                          disabled={c.enquete!.encerrada}
                          style={{
                            textAlign: 'left', padding: '8px 12px', fontSize: 14, borderRadius: 8,
                            border: `1px solid ${escolhida ? '#1565c0' : '#ddd'}`,
                            background: escolhida ? '#e3f2fd' : '#fff',
                            fontWeight: escolhida ? 700 : 400,
                            cursor: c.enquete!.encerrada ? 'default' : 'pointer',
                            opacity: c.enquete!.encerrada && !escolhida ? 0.6 : 1,
                          }}
                        >
                          {escolhida ? '● ' : '○ '}{o.texto}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!c.lido_em && (
                <button onClick={() => marcarLido(c.id)} style={{ marginTop: 8, padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer' }}>✓ Marcar como lido</button>
              )}
            </div>
          ))}
      </section>

      {/* Autorizações e Documentos (aceite digital do responsável) */}
      <section style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 10px' }}>📋 Autorizações e Documentos {termosPendentes > 0 ? <span style={{ background: '#e65100', color: '#fff', borderRadius: 10, fontSize: 12, padding: '1px 8px' }}>{termosPendentes} pendente(s)</span> : null}</h3>
        {termos.length === 0
          ? <div style={{ color: '#888', fontSize: 14 }}>Nenhum documento no momento.</div>
          : termos.map((t) => {
            const pendente = t.status === 'pendente';
            return (
              <div key={t.id} style={{ background: pendente ? '#fff8e1' : '#fff', border: '1px solid #eee', borderLeft: `4px solid ${pendente ? '#e65100' : '#2e7d32'}`, borderRadius: 10, padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <div>
                    <strong>{t.titulo}</strong>
                    <div style={{ fontSize: 12, color: '#888' }}>{TIPO_TERMO_LABEL[t.tipo] || t.tipo}</div>
                  </div>
                  {pendente
                    ? <span style={{ fontSize: 12, color: '#e65100', fontWeight: 700 }}>Aguardando seu aceite</span>
                    : <span style={{ fontSize: 12, color: '#2e7d32', fontWeight: 700 }}>✓ Autorizado {t.assinado_em ? `em ${dataHoraBR(t.assinado_em)}` : ''}</span>}
                </div>
                {t.conteudo && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 13, color: '#1565c0' }}>Ler o documento</summary>
                    <div style={{ fontSize: 13, marginTop: 6, whiteSpace: 'pre-wrap', color: '#444', maxHeight: 240, overflowY: 'auto' }}>{t.conteudo}</div>
                  </details>
                )}
                {pendente && (
                  <button onClick={() => aceitarTermo(t)} style={{ marginTop: 10, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2e7d32', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                    ✔ Li e autorizo
                  </button>
                )}
              </div>
            );
          })}
      </section>

      {/* Desenvolvimento (avaliações pedagógicas / BNCC) */}
      <section style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 10px' }}>🌱 Desenvolvimento</h3>
        {desenvolvimento.length === 0
          ? <div style={{ color: '#888', fontSize: 14 }}>Ainda não há relatórios de desenvolvimento.</div>
          : desenvolvimento.map((a) => (
            <div key={a.id} style={{ background: '#fff', border: '1px solid #eee', borderLeft: '4px solid #2e7d32', borderRadius: 10, padding: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                {a.campo_experiencia
                  ? <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, fontSize: 12, fontWeight: 700, padding: '2px 10px' }}>{a.campo_experiencia}</span>
                  : <strong style={{ fontSize: 14 }}>{a.criterio || 'Relatório'}</strong>}
                <span style={{ fontSize: 12, color: '#999' }}>{dataBR(a.data)}</span>
              </div>
              {a.relatorio && <div style={{ fontSize: 14, marginTop: 6, whiteSpace: 'pre-wrap', color: '#444' }}>{a.relatorio}</div>}
            </div>
          ))}
      </section>

      {/* Diário */}
      <section>
        <h3 style={{ margin: '0 0 10px' }}>📔 Diário do dia a dia</h3>
        {diario.length === 0
          ? <div style={{ color: '#888', fontSize: 14 }}>Ainda não há registros de diário.</div>
          : diario.map((d) => (
            <div key={d.id} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{dataBR(d.data)}</div>
              <CampoDiario campo="presenca" valor={d.presenca} />
              <CampoDiario campo="alimentacao" valor={d.alimentacao} />
              <CampoDiario campo="sono" valor={d.sono} />
              <CampoDiario campo="higiene" valor={d.higiene} />
              <CampoDiario campo="humor" valor={d.humor} />
              <CampoDiario campo="atividades" valor={d.atividades} />
              <CampoDiario campo="ocorrencias" valor={d.ocorrencias} />
              {d.observacoes_gerais && <div style={{ fontSize: 13, marginTop: 6, color: '#555', fontStyle: 'italic' }}>📝 {d.observacoes_gerais}</div>}
            </div>
          ))}
      </section>
    </div>
  );
}
